import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDecimal, formatNumber, type Food, type Meal } from '@jungles/shared';
import { api } from '../lib/api';
import { useApi } from '../lib/hooks';
import { useToast } from '../app/toast';
import { BackLink, ErrorState, Header, Loader, Screen, TabBar } from '../components/Chrome';

/** N5 · Библиотека «Мои продукты» (SPEC §5). В съеденном не учитывается. */

export function LibraryScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, error, loading, reload } = useApi<Food[]>('/api/foods/library');
  const [busy, setBusy] = useState<string | null>(null);

  const meal = (params.get('meal') ?? 'snack') as Meal;
  const date = params.get('date') ?? '';
  const query = `meal=${meal}${date ? `&date=${date}` : ''}`;

  const remove = async (food: Food) => {
    setBusy(food.id);
    try {
      await api.del(`/api/foods/${food.id}`);
      toast('Продукт удалён из библиотеки');
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Screen tabs>
      <Header />
      <BackLink to="/nutrition" label="Питание" />
      <h1>Мои продукты</h1>

      <section className="card">
        <h2>Для следующего раза</h2>
        <p className="nt">
          Библиотека не учитывается в съеденном и ничего не публикует в общий каталог.
        </p>

        {loading ? <Loader /> : null}
        {error ? <ErrorState message={error} onRetry={reload} /> : null}
        {!loading && (data ?? []).length === 0 ? (
          <p className="empty">Пока пусто — продукты попадают сюда при ручном добавлении</p>
        ) : null}

        {(data ?? []).map((food) => (
          <article className="li" key={food.id}>
            <h3>{food.name}</h3>
            <p style={{ margin: '6px 0' }}>
              {formatNumber(food.kcal100)} ккал / 100 г · Б {formatDecimal(food.p100)} Ж{' '}
              {formatDecimal(food.f100)} У {formatDecimal(food.c100)}
              {food.brand ? ` · ${food.brand}` : ''}
            </p>
            <div className="acts">
              <button
                className="b"
                type="button"
                onClick={() => navigate(`/nutrition/food?${query}&foodId=${food.id}`)}
              >
                Выбрать продукт
              </button>
              <button
                className="b d"
                type="button"
                disabled={busy === food.id}
                onClick={() => void remove(food)}
              >
                Удалить
              </button>
            </div>
          </article>
        ))}

        <div className="acts">
          <button
            className="b p"
            type="button"
            onClick={() => navigate(`/nutrition/food?${query}`)}
          >
            Новый продукт
          </button>
        </div>
      </section>

      <TabBar />
    </Screen>
  );
}
